import Box from '@mui/material/Box';
import { useCallback, useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import { useNavigate } from 'react-router-dom';
import FormDialog from 'common/Modal/Modal';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Bank } from "assets/SideBarIcons/bank.svg";
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { BankDepositProfileService } from 'subModule/src/core/services';
import { DeleteData } from 'subModule/src/type';
import Loader from 'layout/Loader';
import { useGetBankDepositsService } from 'subModule/src/services/bankdeposit/useBankDeposit';
import { BankDepositResponse, ChangeStatusBankDepositResponse } from 'subModule/src/services/bankdeposit/interface';
import StyledInput from "common/SearchField/Search";

export default function BankDepositListing() {
    const navigate = useNavigate();
    const [openDeleteModal, setDeleteModal] = useState(false);
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [statusModal, setStatusModal] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [search, setSearch] = useState<any>('');
    const [searchTerm, setSearchTerm] = useState<any>('');
    const [row, setRow] = useState<BankDepositResponse>({});
    const { data: tableData, isFetching, refetch } = searchTerm ? useGetBankDepositsService({ pageNumber: pageNo, size: pageSize, receiptNumber: searchTerm }) : useGetBankDepositsService({ pageNumber: pageNo, size: pageSize });
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, searchTerm]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const onEdit = (row: any) => navigate('/bank-deposit/' + row.id);
    const showRowView = (row: any) => navigate('/bank-deposit/' + row.id);
    const onDelete = (row: any) => {
        setDeleteModal(true);
        setRow(row);
    }
    const clearSearch = () => {
        setSearch('');
        setSearchTerm('');
        if (searchTerm) { handleSearch(''); }
    }
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        setPageNo(1);
        setPageSize(pageSize);
    };
    const handleSearchClick = () => {
        const trimmedSearch = search.trim();
        if (trimmedSearch !== '') handleSearch(trimmedSearch);
    };
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value.trim());
        if (!event.target.value.trim()) handleSearch('');
    };
    const onChangeStatus = (row: any) => {
        setRow(row);
        setStatusModal(true);
    }
    const actionslist = [
        { label: "edit", onClick: onEdit, icon: <EditIcon /> },
        { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
    ];
    const adjustActions = (actions: any[]) => {
        return checkPrivileges('ROLE_BANKDEPOSIT_UPDATE')
            ? (checkPrivileges('ROLE_BANKDEPOSIT_ARCHIVE')
                ? actions
                : actions.filter(action => action.label !== 'delete'))
            : (checkPrivileges('ROLE_BANKDEPOSIT_ARCHIVE')
                ? actions.filter(action => action.label !== 'edit')
                : []);
    }
    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/bank-deposit/create')}
                heading={t('bankDeposits')}
                icon={<Bank height={28} width={28} />}
                btnText={"create"}
                btnType={"create"}
                showCreateBtn={checkPrivileges("ROLE_BANKDEPOSIT_CREATE")}
            />
            {(isFetching || loading) ? <Loader /> : <BackendPaginationTable
                columns={[
                    { id: 'receiptNumber', label: 'receiptNumber', numeric: false },
                    { id: 'bankName', label: 'bankName', numeric: false },
                    { id: 'number', label: 'accountNumber', numeric: false },
                    { id: 'date', label: 'date', numeric: false },
                    { id: 'amount', label: 'amount', numeric: false },
                    ...(checkPrivileges("ROLE_BANKDEPOSIT_ACTIVE") ? [{ id: 'active', label: 'status', numeric: false }] : []),
                    ...(adjustActions(actionslist).length ? [{ id: 'actions', label: 'actions', numeric: false }] : []),
                ]}
                data={tableData?.pages[0]?.data?.content || []}
                pageNo={pageNo}
                pageSize={pageSize}
                tableData={tableData?.pages[0]?.data || {}}
                showCheckbox={false}
                showActions={true}
                enableSorting={true}
                showHeader={true}
                showSearch={true}
                showFilter={true}
                showCreateButton={true}
                actions={adjustActions(actionslist)}
                handleChangePage={handleChangePage}
                handleChangeRows={handleChangeRows}
                onChangeStatus={onChangeStatus}
                rowClick={checkPrivileges("ROLE_BANKDEPOSIT_UPDATE")}
                showRowView={showRowView}
                loadingSearch={false}
                headerComponent={<Grid container spacing={2}>
                    <Grid item xs={12} md={7} lg={5}>
                        <StyledInput
                            fullWidth
                            placeholder={t("receiptNumber")}
                            handleSearchClick={handleSearchClick}
                            handleChange={handleInputChange}
                            value={search}
                            clearSearch={clearSearch}
                        />
                    </Grid>
                </Grid>}
            />}
            {openDeleteModal &&
                <FormDialog
                    open={openDeleteModal}
                    onClose={() => setDeleteModal(false)}
                    title={t('delete') + ' ' + t('bankDeposit')}
                    // icon={<div><Home/></div>}
                    content={
                        <Grid container >
                            <Grid item xs={12}  >
                                <Typography>{t("Are you sure you want to delete?")}</Typography>
                            </Grid>
                        </Grid>
                    }
                    saveFunction={() => {
                        setDeleteModal(false);
                        setLoading(true);
                        BankDepositProfileService.deleteBankDeposit((row as any).id).then((response: DeleteData) => {
                            if (response.success) {
                                if (tableData?.pages[0]?.data?.content?.length === 1 && (pageNo > 1)) setPageNo(1);
                                else refetch();
                                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('bankDeposit') + ' ' + t('deleted') + ' ' + t('successfully')} />, { variant: 'success' });
                            } else {
                                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
                            }
                        }).catch((err: any) => {
                            console.error("===", err);
                            enqueueSnackbar(<AlertMessages text={err?.data?.message || err || "Something went wrong"} />, { variant: 'error' });
                        }).finally(() => setLoading(false));
                    }}
                    submitButtonText="Delete"
                />}
            {statusModal &&
                <FormDialog
                    open={statusModal}
                    onClose={() => setStatusModal(false)}
                    title="Change Status"
                    // icon={<div><Home/></div>}
                    content={
                        <Grid container >
                            <Grid item xs={12}  >
                                <Typography>{t(`Are you sure?`)}</Typography>
                            </Grid>
                        </Grid>
                    }
                    saveFunction={() => {
                        setStatusModal(false);
                        setLoading(true);
                        BankDepositProfileService.changeStatusBankDeposit((row as any).id, row.active ? "inactive" : "active", {}).then((response: ChangeStatusBankDepositResponse) => {
                            if (response.success) {
                                refetch();
                                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('status') + ' ' + t('changed') + ' ' + t('successfully')} />, { variant: 'success' });
                            } else {
                                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
                            }
                        }).catch((err: any) => {
                            console.error("===", err);
                            enqueueSnackbar(<AlertMessages text={err?.data?.message || err || "Something went wrong"} />, { variant: 'error' });
                        }).finally(() => setLoading(false));
                    }}
                    submitButtonText="OK"
                />
            }
        </Box>
    )
}